import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "@/lib/api";
import {
  clearImpersonationMeta,
  readImpersonationMeta,
  writeImpersonationMeta,
  type ImpersonationMeta,
} from "@/lib/impersonation";

export type AuthUser = {
  id: number;
  email: string | null;
  phone: string | null;
  wechat_union_id: string | null;
  display_name: string | null;
  avatar_url: string | null;
  avatar_initial: string;
  last_activity_at: string | null;
  /** 平台管理员角色；null 表示非平台运营账号。 */
  platform_role: string | null;
};

type WechatLoginInput = {
  channel: "mini_program" | "website_qr" | "mp_oauth";
  phone: string;
  union_id: string;
};

type EmailOtpResult = {
  ok: boolean;
  email: string;
  message: string;
  code_sent_recently: boolean;
  cooldown_seconds: number;
};

type SessionTokens = {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
  user: AuthUser;
};

type AuthContextValue = {
  user: AuthUser | null;
  isLoading: boolean;
  impersonation: ImpersonationMeta | null;
  loginWithEmail: (email: string, code: string) => Promise<void>;
  requestEmailOtp: (email: string, options?: { force?: boolean }) => Promise<EmailOtpResult>;
  /** Non-production mock WeChat only. */
  loginWithWechatMock: (input: WechatLoginInput) => Promise<void>;
  logout: () => Promise<void>;
  /** GET /auth/me 并更新上下文（改资料后可选刷新）。 */
  refreshUser: () => Promise<AuthUser>;
  /** 改邮箱成功后写入新 token 与用户。 */
  applySession: (session: SessionTokens) => void;
  /** PATCH 头像/展示名等返回 user 时直接同步。 */
  setAuthUser: (user: AuthUser) => void;
  /** 消费 Magic Link token 并建立会话。 */
  loginWithMagicLink: (token: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function syncImpersonationFromMe(payload: { impersonation?: ImpersonationMeta | null }) {
  if (payload.impersonation) {
    writeImpersonationMeta(payload.impersonation);
    return payload.impersonation;
  }
  clearImpersonationMeta();
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [impersonation, setImpersonation] = useState<ImpersonationMeta | null>(() => readImpersonationMeta());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!api.getAccessToken()) {
      setIsLoading(false);
      return;
    }

    api
      .fetch<{ user: AuthUser; impersonation?: ImpersonationMeta }>("/auth/me")
      .then((payload) => {
        setUser(payload.user);
        setImpersonation(syncImpersonationFromMe(payload));
        api.startTokenRefreshScheduler();
      })
      .catch(() => {
        setUser(null);
        setImpersonation(null);
        clearImpersonationMeta();
      })
      .finally(() => setIsLoading(false));
  }, []);

  async function requestEmailOtp(email: string, options?: { force?: boolean }) {
    return api.fetch<EmailOtpResult>("/auth/email/otp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, force: options?.force === true }),
    });
  }

  async function loginWithEmail(email: string, code: string) {
    const response = await api.fetch<{
      access_token: string;
      refresh_token: string;
      expires_in?: number;
      user: AuthUser;
    }>("/auth/email/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, code }),
    });

    clearImpersonationMeta();
    api.setTokens(response);
    api.startTokenRefreshScheduler();
    setUser(response.user);
    setImpersonation(null);
  }

  async function loginWithWechatMock({ channel, phone, union_id }: WechatLoginInput) {
    const response = await api.fetch<{
      access_token: string;
      refresh_token: string;
      expires_in?: number;
      user: AuthUser;
    }>(`/auth/wechat/${channel}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone, union_id }),
    });

    clearImpersonationMeta();
    api.setTokens(response);
    api.startTokenRefreshScheduler();
    setUser(response.user);
    setImpersonation(null);
  }

  async function logout() {
    const refreshToken = api.getRefreshToken();
    try {
      if (refreshToken) {
        await api.fetch("/auth/logout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      }
    } finally {
      clearImpersonationMeta();
      api.clearTokens();
      setUser(null);
      setImpersonation(null);
    }
  }

  async function refreshUser() {
    const payload = await api.fetch<{ user: AuthUser; impersonation?: ImpersonationMeta }>("/auth/me");
    setUser(payload.user);
    setImpersonation(syncImpersonationFromMe(payload));
    return payload.user;
  }

  function applySession({ access_token, refresh_token, expires_in, user: sessionUser }: SessionTokens) {
    api.setTokens({ access_token, refresh_token, expires_in });
    api.startTokenRefreshScheduler();
    setUser(sessionUser);
    setImpersonation(readImpersonationMeta());
  }

  function setAuthUser(nextUser: AuthUser) {
    setUser(nextUser);
  }

  async function loginWithMagicLink(token: string) {
    const response = await api.fetch<SessionTokens>("/auth/magic_link/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });

    clearImpersonationMeta();
    api.setTokens(response);
    api.startTokenRefreshScheduler();
    setUser(response.user);
    setImpersonation(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        impersonation,
        loginWithEmail,
        requestEmailOtp,
        loginWithWechatMock,
        logout,
        refreshUser,
        applySession,
        setAuthUser,
        loginWithMagicLink,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
